import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Calendar, User, ArrowRight, Share2, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { BLOGS } from "../data/blogs";
import { renderBlogBody } from "../lib/blogRenderer";

export const LearnPage = () => {
  const categories = [...new Set(BLOGS.map(b => b.category))];
  return (
    <div data-testid="learn-page">
      <section className="bg-gradient-to-br from-[#EAF5FD] to-white">
        <div className="ol-container py-10 lg:py-14">
          <Breadcrumbs items={[{ label: "Learn" }]}/>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold font-heading text-[#273B59] leading-[1.05]">Learn. Plan. Borrow smart.</h1>
          <p className="mt-4 text-lg text-[#475569] max-w-2xl">Expert guides on personal loans, business loans, credit scores, and financial planning — written by India's lending specialists.</p>
        </div>
      </section>
      <section className="ol-section">
        <div className="ol-container">
          <div className="flex flex-wrap gap-2 mb-8">
            {categories.map(c => <Badge key={c} className="bg-[#EAF5FD] text-[#1088D4] hover:bg-[#1088D4] hover:text-white border-0 cursor-default px-3 py-1.5">{c}</Badge>)}
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {BLOGS.map((b,i) => (
              <Link key={b.slug} to={`/learn/${b.slug}`} data-testid={`blog-card-${i}`} className="ol-card-hover bg-white border border-[#E2E8F0] rounded-xl p-6 flex flex-col">
                <Badge className="bg-[#EAF5FD] text-[#1088D4] hover:bg-[#EAF5FD] border-0 w-fit mb-3">{b.category}</Badge>
                <h3 className="text-lg font-bold text-[#273B59] font-heading leading-snug">{b.title}</h3>
                <p className="mt-3 text-sm text-[#475569] line-clamp-3">{b.excerpt}</p>
                <div className="mt-5 pt-4 border-t border-[#E2E8F0] flex items-center justify-between text-xs text-[#475569]">
                  <span className="flex items-center gap-1"><Calendar size={12}/> {b.date}</span>
                  <span>{b.readTime}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export const BlogPostPage = () => {
  const { slug } = useParams();
  const post = BLOGS.find(b => b.slug === slug);
  if (!post) return <Navigate to="/learn" replace/>;
  const related = BLOGS.filter(b => b.slug !== slug).slice(0,3);

  return (
    <article data-testid="blog-post">
      <div className="ol-container py-10">
        <Breadcrumbs items={[{ label: "Learn", to: "/learn" }, { label: post.title }]}/>
        <div className="mt-6 max-w-3xl mx-auto">
          <Badge className="bg-[#EAF5FD] text-[#1088D4] hover:bg-[#EAF5FD] border-0">{post.category}</Badge>
          <h1 className="mt-4 text-4xl sm:text-5xl font-bold font-heading text-[#273B59] leading-tight">{post.title}</h1>
          <div className="mt-5 flex flex-wrap items-center gap-5 text-sm text-[#475569]">
            <span className="flex items-center gap-2"><div className="w-8 h-8 rounded-full bg-[#1088D4] text-white flex items-center justify-center text-xs font-semibold">OE</div>{post.author}</span>
            <span className="flex items-center gap-1"><Calendar size={14}/> {post.date}</span>
            <span>{post.readTime}</span>
            <button data-testid="share-btn" className="flex items-center gap-1 hover:text-[#1088D4]"><Share2 size={14}/> Share</button>
          </div>
          <div className="mt-10 prose max-w-none">
            {renderBlogBody(post.body)}
          </div>

          <div className="mt-12 bg-gradient-to-br from-[#EAF5FD] to-white rounded-2xl p-6 sm:p-8 border border-[#1088D4]/20">
            <h3 className="text-xl font-bold font-heading text-[#273B59]">Ready to apply for a loan?</h3>
            <p className="mt-2 text-sm text-[#475569]">Get pre-approved offers from 16+ lenders in minutes.</p>
            <Link to="/apply"><Button className="mt-4 ol-btn-primary">Apply Now <ArrowRight size={14} className="ml-1.5"/></Button></Link>
          </div>

          <div className="mt-12">
            <h3 className="text-2xl font-bold font-heading text-[#273B59]">Related Articles</h3>
            <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {related.map(r => (
                <Link key={r.slug} to={`/learn/${r.slug}`} className="bg-white border border-[#E2E8F0] rounded-xl p-5 ol-card-hover">
                  <Badge className="bg-[#EAF5FD] text-[#1088D4] hover:bg-[#EAF5FD] border-0 text-[10px]">{r.category}</Badge>
                  <h4 className="mt-2 font-semibold text-[#273B59] text-sm leading-snug">{r.title}</h4>
                </Link>
              ))}
            </div>
          </div>

          <Link to="/learn" className="mt-10 inline-flex items-center gap-2 text-sm text-[#1088D4] font-semibold"><ArrowLeft size={14}/> Back to all articles</Link>
        </div>
      </div>
    </article>
  );
};
